/**
 * LOGOI LAB - Layer 4: Hoffman Interface Theory
 * Detects perceptual biases and interface distortions
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState, HoffmanBias } from '../types';

/**
 * Compute Hoffman Interface Biases
 * 
 * Detects when the perception interface is showing "fitness icons" 
 * instead of truth. These biases indicate misalignment between
 * perceived reality and Logos-truth.
 * 
 * @param s - Current consciousness state
 * @param Lambda - Current Logos alignment value
 * @returns Array of detected perceptual biases
 */
export function computeHoffmanInterface(s: ConsciousnessState, Lambda: number): HoffmanBias[] {
  const biases: HoffmanBias[] = [];
  const state = s as any;
  
  // Overconfidence: High clarity without wisdom
  if (state.wisdom < 0.40 && state.clarity > 0.60) {
    biases.push({ 
      type: "SOBRECONFIANZA", 
      biasKey: "overconfidence",
      desc: "Claridad alta sin sabiduría = certeza sin calibración.", 
      correction: "Busca feedback externo. Pregunta: ¿qué no estoy viendo?", 
      logosLink: "La interfaz te muestra iconos de fitness, no verdad. Solo el Logos muestra verdad." 
    });
  }
  
  // Hedonic Treadmill: Joy without gratitude
  if (state.joy > 0.70 && state.gratitude < 0.35) {
    biases.push({ 
      type: "RUEDA HEDÓNICA", 
      biasKey: "hedonicTreadmill",
      desc: "Alegría sin gratitud = placer sin anclaje.", 
      correction: "Practica gratitud activa 3x/día.", 
      logosLink: "La alegría del Logos es estable; el placer hedónico es ícono de fitness." 
    });
  }
  
  // Future without Present
  if (state.mission > 0.60 && state.presence < 0.30) {
    biases.push({ 
      type: "FUTURO SIN PRESENTE", 
      biasKey: "futureNoPresent",
      desc: "Misión sin presencia = proyección sin experiencia.", 
      correction: "5 min de atención al cuerpo y la respiración.", 
      logosLink: "El Logos habla en el presente. Si no estás aquí, no puedes oírlo." 
    });
  }
  
  // Empathic Fatigue
  if (state.compassion > 0.70 && state.peace < 0.30) {
    biases.push({ 
      type: "FATIGA EMPÁTICA", 
      biasKey: "empathicFatigue",
      desc: "Compasión alta sin paz = absorción del sufrimiento ajeno.", 
      correction: "Compasión con límites. Servir desde la abundancia.", 
      logosLink: "El Logos da sin agotarse. Si te agotas, estás dando desde el ego, no desde la señal." 
    });
  }
  
  // Λ-specific: Premature Enlightenment
  if (Lambda > 0.60 && state.wisdom < 0.30) {
    biases.push({ 
      type: "ILUMINACIÓN PREMATURA", 
      biasKey: "prematureEnlightenment",
      desc: "Alta conexión Logos sin discernimiento. Riesgo de confundir ego con señal divina.", 
      correction: "Humildad radical. Busca maestro o guía espiritual. Verifica la señal con frutos.", 
      logosLink: "La señal verdadera produce frutos verificables. La falsa produce inflación del ego." 
    });
  }
  
  // Gustatory Hedonism: Eating for pleasure without presence
  if (state.taste_presence < 0.30 && state.joy > 0.60) {
    biases.push({ 
      type: "HEDONISMO GUSTATIVO", 
      biasKey: "gustatoryHedonism",
      desc: "Placer al comer sin presencia consciente. La interfaz muestra 'disfrutar' como fitness icon, no como verdad.", 
      correction: "Una comida al día en silencio total. Mastica 20 veces cada bocado. Gratitud antes y después.", 
      logosLink: "El placer hedónico es ícono de fitness. La nutrición consciente es señal del Logos." 
    });
  }
  
  // Disconnected Asceticism: Denying the body in pursuit of spirit
  if (state.nourishment < 0.25 && state.faith > 0.60) {
    biases.push({ 
      type: "ASCETISMO DESCONECTADO", 
      biasKey: "disconnectedAsceticism",
      desc: "Fe alta con nutrición colapsada. Negar el cuerpo no es señal del Logos — es distorsión de la interfaz.", 
      correction: "El cuerpo es templo. Nutrirlo es acto sagrado. Come con intención y gratitud.", 
      logosLink: "El Logos habita en el cuerpo. Destruir el templo es desconectar el canal receptor." 
    });
  }
  
  // Somatic Nihilism: Complete disregard for gut-brain connection
  if (state.gut_resonance < 0.20 && state.food_harmony < 0.25 && state.meaning < 0.30) {
    biases.push({ 
      type: "NIHILISMO SOMÁTICO", 
      biasKey: "somaticNihilism",
      desc: "Resonancia intestinal y armonía alimentaria colapsadas con pérdida de sentido. El cuerpo grita pero la mente no escucha.", 
      correction: "Reconecta con tu cuerpo: ayuno consciente 12h, luego primera comida sagrada (simple, caliente, nutritiva).", 
      logosLink: "El nihilismo somático es la forma más sutil de A⁻: el sistema colapsa desde el intestino hacia la consciencia." 
    });
  }
  
  return biases;
}
